package com.sweetlime.dungeonamigo.presentation.features.compendium

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.cache.Monsters
import com.sweetlime.dungeonamigo.compendium.CompendiumRepository
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import org.koin.compose.koinInject

data class MonsterDetailScreen(val slug: String) : Screen {

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val repository: CompendiumRepository = koinInject()
        val monster = remember(slug) { repository.getMonsterBySlug(slug) }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(BrandColors.Background)
        ) {
            MonsterTopBar(title = monster?.name ?: slug, onBack = { navigator.pop() })
            if (monster != null) {
                MonsterStatBlock(monster)
            } else {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text("Monster not found", color = BrandColors.TextSecondary)
                }
            }
        }
    }
}

@Composable
private fun MonsterTopBar(title: String, onBack: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(BrandColors.Surface)
            .padding(horizontal = 8.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        IconButton(onClick = onBack) {
            Icon(Icons.AutoMirrored.Filled.ArrowBack, "Back", tint = BrandColors.TextPrimary)
        }
        Text(title, color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold, fontSize = 18.sp)
    }
}

@Composable
private fun MonsterStatBlock(monster: Monsters) {
    val cr = monster.challenge_rating
    val crColor = if (crHighThreat(cr)) BrandColors.Ember else BrandColors.Parchment

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth()
        ) {
            val subtitle = listOfNotNull(monster.size, monster.type, monster.alignment)
                .joinToString(", ")
            Text(subtitle, color = BrandColors.TextSecondary, fontSize = 13.sp, modifier = Modifier.weight(1f))

            cr?.let {
                Surface(
                    color = crColor.copy(alpha = 0.2f),
                    shape = MaterialTheme.shapes.small
                ) {
                    Text(
                        "CR $it",
                        color = crColor,
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                    )
                }
            }
        }

        HorizontalDivider(color = BrandColors.SurfaceLight)

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            StatChip("AC", monster.armor_class?.toString())
            StatChip("HP", monster.hit_points?.toString())
            StatChip("HD", monster.hit_dice)
        }

        monster.speed?.let { MonsterStatRow("Speed", it) }

        HorizontalDivider(color = BrandColors.SurfaceLight)

        monster.description?.takeIf { it.isNotBlank() }?.let { desc ->
            Text(desc, color = BrandColors.TextPrimary, fontSize = 14.sp, lineHeight = 22.sp)
        }

        monster.actions?.takeIf { it.isNotBlank() }?.let { actions ->
            HorizontalDivider(color = BrandColors.SurfaceLight)
            Text(
                "Actions",
                color = BrandColors.Parchment,
                fontWeight = FontWeight.SemiBold,
                fontSize = 15.sp
            )
            Text(actions, color = BrandColors.TextPrimary, fontSize = 14.sp, lineHeight = 22.sp)
        }
    }
}

@Composable
private fun StatChip(label: String, value: String?) {
    if (value.isNullOrBlank()) return
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(value, color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold, fontSize = 16.sp)
        Text(label, color = BrandColors.TextSecondary, fontSize = 11.sp)
    }
}

@Composable
private fun MonsterStatRow(label: String, value: String) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, color = BrandColors.TextSecondary, fontSize = 13.sp, modifier = Modifier.weight(0.4f))
        Text(value, color = BrandColors.TextPrimary, fontSize = 13.sp, modifier = Modifier.weight(0.6f))
    }
}

private fun crHighThreat(cr: String?): Boolean {
    if (cr == null) return false
    val num = cr.toDoubleOrNull() ?: return false
    return num >= 10.0
}